import type { Business, BusinessPatch, Me, TaxRegion } from '../../api/client'
import { parseWholeNumber } from '../../catalog/format'
import { displayPhone } from '../../clients/phone'

export interface BusinessForm {
  trading_name: string
  address_line1: string
  address_line2: string
  city: string
  postcode: string
  phone: string
  tax_region: TaxRegion | ''
  confirmation_note: string
}

export function toBusinessForm(business: Business | null): BusinessForm {
  return {
    trading_name: business?.trading_name ?? '',
    address_line1: business?.address_line1 ?? '',
    address_line2: business?.address_line2 ?? '',
    city: business?.city ?? '',
    postcode: business?.postcode ?? '',
    phone: displayPhone(business?.phone),
    tax_region: business?.tax_region ?? '',
    confirmation_note: business?.confirmation_note ?? '',
  }
}

function orNull(value: string): string | null {
  const text = value.trim()
  return text === '' ? null : text
}

// cancellationNoticeError says what the form can tell without asking the API:
// a notice that isn't a whole number ("1e6", "12.5") is refused here rather
// than saved as 1 or 12. Its limits are the API's to say.
export function cancellationNoticeError(input: string): string {
  return parseWholeNumber(input) === null ? 'Enter a whole number of hours, like 24.' : ''
}

// toBusinessPatch sends every field; blanks clear optional ones. How long
// records are kept isn't hers to set: 7 years for everyone (2026-10-28).
export function toBusinessPatch(form: BusinessForm): BusinessPatch {
  const patch: BusinessPatch = {
    trading_name: form.trading_name.trim(),
    address_line1: orNull(form.address_line1),
    address_line2: orNull(form.address_line2),
    city: orNull(form.city),
    postcode: orNull(form.postcode),
    phone: orNull(form.phone),
    confirmation_note: orNull(form.confirmation_note),
  }
  // Nothing chosen yet: leave tax_region out of the patch rather than send
  // an empty string the API would reject.
  if (form.tax_region !== '') {
    patch.tax_region = form.tax_region
  }
  return patch
}

// meWithBusiness updates the signed-in professional's business summary from a
// saved business, so Settings shows the new name and location without another request.
export function meWithBusiness(me: Me, saved: Business): Me {
  return {
    ...me,
    business: {
      slug: saved.slug,
      trading_name: saved.trading_name,
      address_line1: saved.address_line1,
      city: saved.city,
    },
  }
}

// outwardPostcode is the first half of a UK postcode, the same rule the
// booking page uses ("SW4" of "SW4 7AB").
function outwardPostcode(postcode: string | null): string {
  const pc = (postcode ?? '').trim().toUpperCase()
  const space = pc.indexOf(' ')
  if (space > 0) return pc.slice(0, space)
  return pc.length > 3 ? pc.slice(0, -3) : pc
}

// locationLine previews where the booking page says she is: the full address
// when she has switched that on, otherwise her area, town and postcode district.
export function locationLine(b: Business): string {
  const v = (s: string | null) => (s ?? '').trim()
  const join = (parts: string[]) => parts.filter((p) => p !== '').join(', ')
  if (b.show_full_address) {
    return join([v(b.address_line1), v(b.address_line2), v(b.city), v(b.postcode)])
  }
  return join([v(b.public_area), `${v(b.city)} ${outwardPostcode(v(b.postcode))}`.trim()])
}
